import {
  LayoutDashboard,
  Users,
  Settings,
  ShoppingBag,
  Store,
  Activity,
  Star,
  KeyRound,
  Bell,
  Package,
  CalendarClock,
  ShieldAlert,
  MessageSquare,
  AlertTriangle,
  BarChart3,
  Globe,
  Building2,
  Wallet,
  Calculator,
  Plug,
} from 'lucide-react';

/**
 * Admin Menu Configuration
 *
 * Two modes:
 * 1. Hub mode (no seller selected) — global/admin items
 * 2. Seller-scoped mode (seller selected) — seller features
 */

// ─── Seller-Scoped Menu (shown when a seller is selected) ───────────────────

export const sellerMenuItems = [
  {
    label: 'Dashboard',
    icon: LayoutDashboard,
    path: '/dashboard',
    description: 'Seller overview & insights',
  },
  {
    label: 'Orders & Sales',
    icon: ShoppingBag,
    path: '/sales',
    description: 'Order sync, revenue & sales notifications',
  },
  {
    label: 'Product Listings',
    icon: Package,
    path: '/products',
    description: 'Synced product listings & status monitoring',
  },
  {
    label: 'Hijack Monitor',
    icon: ShieldAlert,
    path: '/hijack-alerts',
    description: 'Buy box hijack detection & alerts',
  },
  {
    label: 'Reviews & Feedback',
    icon: MessageSquare,
    path: '/reviews-feedback',
    description: 'Customer reviews & seller feedback received',
  },
  {
    label: 'Review Requests',
    icon: Star,
    path: '/review-requests',
    description: 'Request product reviews for delivered orders',
  },
  {
    label: 'Policy Warnings',
    icon: AlertTriangle,
    path: '/policy-warnings',
    description: 'Account health & policy violation alerts',
  },
  {
    label: 'Ad Performance',
    icon: BarChart3,
    path: '/ad-performance',
    description: 'Sponsored ads performance tracking',
  },
  {
    label: 'Reconciliation',
    icon: Wallet,
    path: '/settlement-reports',
    description: 'Amazon settlement reports & disbursement reconciliation',
  },
  {
    label: 'Pricing Calculator',
    icon: Calculator,
    path: '/pricing-calculator',
    description: 'Estimate net bank credit before listing a product',
  },
];

// ─── Seller Top Nav (shown as horizontal bar below header) ──────────────────

export const sellerTopNavItems = [
  { label: 'Home', icon: LayoutDashboard, path: '/dashboard', isSidebarView: true },
  { label: 'Seller Info', icon: Building2, path: '/settings/seller-info' },
  { label: 'Marketplaces', icon: Globe, path: '/marketplaces' },
  { label: 'Delivery Activity', icon: Activity, path: '/delivery-activity' },
  { label: 'Scheduler', icon: CalendarClock, path: '/settings/scheduler-status' },
];

// Paths where sidebar should be shown (Dashboard + all sidebar feature pages)
export const sidebarVisiblePaths = [
  '/dashboard', '/sales', '/products', '/hijack-alerts',
  '/reviews-feedback', '/review-requests', '/policy-warnings',
  '/ad-performance', '/settlement-reports', '/pricing-calculator', '/settings/notification-config',
];

// ─── Hub Top Nav (horizontal bar, no sidebar) ──────────────────────────────

export const hubTopNavItems = [
  { label: 'My Sellers', icon: Store, path: '/sellers' },
  { label: 'Users', icon: Users, path: '/users', adminOnly: true },
  { label: 'Delivery Activity', icon: Activity, path: '/delivery-activity', adminOnly: true },
  { label: 'Scheduler Status', icon: CalendarClock, path: '/settings/scheduler-status', adminOnly: true },
  { label: 'Settings', icon: Settings, path: '/settings/whatsapp' },
];

// Legacy exports for compatibility
export const hubMenuItems = hubTopNavItems;
export const menuItems = sellerMenuItems;

// Seller bottom menu
export const sellerBottomMenuItems = [
  {
    label: 'Notification Config',
    icon: Bell,
    path: '/settings/notification-config',
    description: 'Configure notification types & channels',
  },
];

export const hubBottomMenuItems = [];

// Legacy export
export const bottomMenuItems = sellerBottomMenuItems;

// Branding configuration
export const branding = {
  logo: 'eComm Notify',
  subtitle: 'Notifications',
  color: 'amazon',
};

// Function to filter menu items based on user role/permissions
export const getFilteredMenuItems = (userRole) => {
  const rolePermissions = {
    admin: [
      'dashboard', 'sales', 'sellers', 'seller-accounts', 'products', 'hijack-alerts',
      'reviews-feedback', 'review-requests', 'policy-warnings', 'ad-performance',
      'delivery-activity', 'marketplaces', 'users', 'settings',
    ],
    manager: ['dashboard', 'sellers', 'sales', 'products', 'delivery-activity', 'marketplaces'],
    staff: ['dashboard', 'sellers'],
  };

  const allowedPaths = rolePermissions[userRole] || rolePermissions.staff;

  return sellerMenuItems.filter((item) => {
    const routeName = item.path.split('/').pop();
    return allowedPaths.includes(routeName);
  });
};
